'use es6';

import I18n from 'I18n';
import PropTypes from 'prop-types';
import { HR } from 'foundations-components/transitional/components/HR';
import { Small } from 'foundations-components/transitional/components/Small';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { Conditions, FilterRefTypes } from '../../schema/filter-records';
import { EventDateIntervalFilterTile } from './components/event-date-filter/EventDateIntervalFilterTile';
import { ExpressionFieldItem, StagedExpressionFieldFilterTile } from './components/expression-field-filter';
import { FieldFilterItem, StagedFieldFilterTile } from './components/field-filter';
import { ListFilterItem, StagedListFilterTile } from './components/list-filter';
import { PropertyFilterItem, StagedPropertyFilterTile } from './components/property-filter';
import SnowflakeFilterConditionSelect from './components/SnowflakeFilterConditionSelect';
import SnowflakeFilterGroup from './components/SnowflakeFilterGroup';
import SnowflakeFilterList from './components/SnowflakeFilterList';
import SnowflakeFilterSeparator from './components/SnowflakeFilterSeparator';
import SnowflakeFilterTile from './components/SnowflakeFilterTile';
import { useDatasetDefinition, useEventDateInterval, useFiltering } from './components/SourceMeta';
import { createDefaultCustomLogic, getGroupIds, isExpressionFieldFilter, isFieldFilter, isListFilter } from './filter-utils';
import LogicEditor from './LogicEditor';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const EventDateFilterSection = ({
  readOnly,
  onChange
}) => {
  const eventDateInterval = useEventDateInterval();
  if (!eventDateInterval) {
    return null;
  }
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx("small", {
      use: "help",
      children: I18n.text('reporting-snowflake.filter-editor.event-date-filter.description')
    }), /*#__PURE__*/_jsx(EventDateIntervalFilterTile, {
      value: eventDateInterval,
      readOnly: readOnly,
      onChange: onChange
    }), /*#__PURE__*/_jsx(HR, {})]
  });
};
const ConditionControl = ({
  readOnly,
  onChange
}) => {
  const filtering = useFiltering();
  const {
    condition,
    logic
  } = filtering;
  const handleConditionChange = nextCondition => {
    const nextLogic = nextCondition === Conditions.CUSTOM ? createDefaultCustomLogic(filtering) : logic;
    onChange(filtering.set('condition', nextCondition).set('logic', nextLogic));
  };
  const hasFilterGroupsOmittedFromRules = () => {
    if (condition === Conditions.CUSTOM && filtering.groups && filtering.groups.length > 0) {
      if (!logic) {
        return true;
      }
      const matchedStrings = logic.match(/\b(\d+)\b/g) || [];
      const groupsMentionedInLogic = matchedStrings.map(id => Number(id));
      return filtering.groups.some(group => !groupsMentionedInLogic.includes(Number(group.id)));
    }
    return false;
  };
  const customFilterGroupsWarning = hasFilterGroupsOmittedFromRules() ? I18n.text('reporting-snowflake.filter-editor.has-filters-groups-not-in-custom-logic') : null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormControl, {
      label: I18n.text('reporting-snowflake.filter-editor.filter-condition-label'),
      children: /*#__PURE__*/_jsx(SnowflakeFilterConditionSelect, {
        condition: condition,
        readOnly: readOnly,
        onChange: handleConditionChange
      })
    }), /*#__PURE__*/_jsx(Small, {
      use: "help",
      children: customFilterGroupsWarning
    })]
  });
};
const LogicControl = ({
  readOnly,
  onChange
}) => {
  const filtering = useFiltering();
  const {
    condition,
    logic
  } = filtering;
  const groupIds = getGroupIds(filtering);
  const handleFilterLogicChange = ({
    value: nextValue
  }) => {
    let nextFiltering = filtering.set('logic', nextValue);
    if (!nextValue || nextValue.trim() === '') {
      nextFiltering = nextFiltering.set('condition', Conditions.AND);
    }
    onChange(nextFiltering);
  };
  const showLogic = condition === Conditions.CUSTOM;
  if (!showLogic) {
    return null;
  }
  return /*#__PURE__*/_jsx(View, {
    marginBlockStart: "300",
    as: "div",
    children: /*#__PURE__*/_jsx(LogicEditor, {
      value: logic,
      validIds: groupIds,
      readOnly: readOnly,
      onChange: handleFilterLogicChange
    })
  });
};
const FilterGroups = ({
  readOnly
}) => {
  const datasetDefinition = useDatasetDefinition();
  const filtering = useFiltering();
  const {
    condition,
    groups
  } = filtering;
  const snowflakeGroups = groups.map(({
    filters
  }, groupIndex) => filters.map((filter, filterIndex) => {
    if (isFieldFilter(filter) && datasetDefinition &&
    // The property used in this filter may have been deleted
    datasetDefinition.getIn(['fields', filter.field.name])) {
      return /*#__PURE__*/_jsx(FieldFilterItem, {
        filter: filter,
        groupIndex: groupIndex,
        filterIndex: filterIndex,
        readOnly: readOnly
      }, filterIndex);
    } else if (isListFilter(filter)) {
      return /*#__PURE__*/_jsx(ListFilterItem, {
        filter: filter,
        groupIndex: groupIndex,
        filterIndex: filterIndex,
        readOnly: readOnly
      }, filterIndex);
    } else if (isExpressionFieldFilter(filter)) {
      return /*#__PURE__*/_jsx(ExpressionFieldItem, {
        filter: filter,
        groupIndex: groupIndex,
        filterIndex: filterIndex,
        readOnly: readOnly
      }, filterIndex);
    } else {
      return /*#__PURE__*/_jsx(PropertyFilterItem, {
        filter: filter,
        groupIndex: groupIndex,
        filterIndex: filterIndex,
        readOnly: readOnly
      }, filterIndex);
    }
  }));
  return snowflakeGroups.map((filtersInGroup, groupIndex) => {
    return /*#__PURE__*/_jsx(SnowflakeFilterTile, {
      id: groupIndex + 1,
      isGrouped: filtersInGroup.count() > 1,
      readOnly: readOnly,
      showSeparator: condition !== Conditions.CUSTOM,
      separator: condition !== Conditions.CUSTOM && groupIndex < snowflakeGroups.length - 1 && /*#__PURE__*/_jsx(SnowflakeFilterSeparator, {
        condition: condition,
        readOnly: true
      }),
      children: /*#__PURE__*/_jsx(SnowflakeFilterGroup, {
        groupIndex: groupIndex,
        readOnly: readOnly,
        children: filtersInGroup
      })
    }, groupIndex);
  });
};
const StagedFilterSection = () => {
  const datasetDefinition = useDatasetDefinition();
  const {
    stagedFilters
  } = useFiltering();
  const renderStagedFilterTile = (filterRef, stagedFilterIndex) => {
    const {
      type,
      field: fieldName
    } = filterRef;
    if (type === FilterRefTypes.DATASET_FIELD) {
      if (datasetDefinition && !datasetDefinition.getIn(['fields', fieldName])) {
        return /*#__PURE__*/_jsx(StagedPropertyFilterTile, {
          index: stagedFilterIndex,
          filterRef: {
            table: '__DATASET__',
            property: fieldName
          }
        }, stagedFilterIndex);
      }
      return /*#__PURE__*/_jsx(StagedFieldFilterTile, {
        index: stagedFilterIndex,
        filterRef: filterRef
      }, stagedFilterIndex);
    }
    if (type === FilterRefTypes.LIST) {
      return /*#__PURE__*/_jsx(StagedListFilterTile, {
        index: stagedFilterIndex,
        filterRef: filterRef
      }, stagedFilterIndex);
    }
    if (type === FilterRefTypes.PROPERTY) {
      return /*#__PURE__*/_jsx(StagedPropertyFilterTile, {
        index: stagedFilterIndex,
        filterRef: filterRef
      }, stagedFilterIndex);
    }
    if (type === FilterRefTypes.EXPRESSION_FIELD) {
      return /*#__PURE__*/_jsx(StagedExpressionFieldFilterTile, {
        index: stagedFilterIndex,
        filterRef: filterRef
      }, stagedFilterIndex);
    }
    return null;
  };
  const stagedFilterTiles = stagedFilters.map(renderStagedFilterTile);
  if (stagedFilterTiles.isEmpty()) {
    return null;
  }
  return /*#__PURE__*/_jsxs(View, {
    marginBlockStart: "400",
    as: "div",
    children: [/*#__PURE__*/_jsx(FormLabel, {
      children: I18n.text('reporting-snowflake.filter-editor.inactive-filters')
    }), /*#__PURE__*/_jsx(SnowflakeFilterList, {
      "data-test-id": "staged-filter-list",
      children: stagedFilterTiles
    })]
  });
};
const BaseFilterEditor = ({
  mode = 'edit',
  onChange,
  onUpdateEventDateInterval
}) => {
  const readOnly = mode === 'display';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(EventDateFilterSection, {
      readOnly: readOnly,
      onChange: onUpdateEventDateInterval
    }), /*#__PURE__*/_jsx(ConditionControl, {
      readOnly: readOnly,
      onChange: onChange
    }), /*#__PURE__*/_jsx(LogicControl, {
      readOnly: readOnly,
      onChange: onChange
    }), /*#__PURE__*/_jsx(View, {
      marginBlockStart: "400",
      as: "div",
      children: /*#__PURE__*/_jsx(SnowflakeFilterList, {
        "data-test-id": "applied-filter-list",
        children: /*#__PURE__*/_jsx(FilterGroups, {
          readOnly: readOnly
        })
      })
    }), !readOnly && /*#__PURE__*/_jsx(StagedFilterSection, {})]
  });
};
BaseFilterEditor.propTypes = {
  mode: PropTypes.oneOf(['edit', 'display']),
  onChange: PropTypes.func,
  onUpdateEventDateInterval: PropTypes.func
};
export default BaseFilterEditor;